import MobileJobActions from './MobileJobActions';
import useMobileSchedule from '../../../hooks/useMobileSchedule';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
    FiHome, FiClock, FiSearch, FiX, FiChevronDown,
    FiRefreshCw, FiAlertTriangle, FiPackage,
} from 'react-icons/fi';
import { GiWoodBeam } from 'react-icons/gi';
import { BrickWall, Grid2X2, Flame, ArrowDownNarrowWide, ArrowUpNarrowWide, ArrowDownAZ, ArrowUpAZ, Hash } from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const QUOTE_COLORS = {
    roof:   { bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-200',   icon: <FiHome className="w-3.5 h-3.5" />, label: 'Roof' },
    wall:   { bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    icon: <BrickWall className="w-3.5 h-3.5" />, label: 'Wall' },
    floor:  { bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  icon: <Grid2X2 className="w-3.5 h-3.5" />, label: 'Floor' },
    timber: { bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  icon: <GiWoodBeam className="w-3.5 h-3.5" />, label: 'Timber' },
};

const formatDate = (d) => {
    if (!d) return null;
    return new Date(d).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
};

// ─── Single draggable job card ────────────────────────────────────────────────
const JobCard = ({ job, onDragStart, onMobileAdd }) => {
    const isMobile = useMobileSchedule();
    const qc = QUOTE_COLORS[job.quoteType] || QUOTE_COLORS.roof;
    const isUrgent = job.urgentQuote || job.urgentDesign;

    return (
        <div
            draggable={!isMobile}
            onDragStart={(e) => {
                if (isMobile) { e.preventDefault(); return; }
                onDragStart(e, job);
                // ghost image styling
                e.dataTransfer.effectAllowed = 'copy';
            }}
            className={`
                relative flex flex-col gap-1.5 p-3 rounded-xl border-2 cursor-grab active:cursor-grabbing
                bg-white shadow-sm hover:shadow-md transition-all select-none
                ${isUrgent ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-200 hover:border-indigo-300'}
            `}
        >
            {/* Urgent badge */}
            {isUrgent && (
                <span className="absolute -top-2 -right-1.5 flex items-center gap-0.5 px-1.5 py-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full">
                    <Flame className="w-2.5 h-2.5" />
                    URGENT
                </span>
            )}

            {/* Top row: Job ID + quote chip */}
            <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-gray-900 truncate flex flex-row items-center gap-1"><Hash className='h-3 w-3 text-gray-600' />{job.childUniqueId || job.uniqueId}</span>
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold border flex-shrink-0 ${qc.bg} ${qc.text} ${qc.border}`}>
                    {qc.icon}
                    {qc.label}
                </span>
            </div>

            {/* Customer */}
            <p className="text-[11px] text-gray-500 truncate leading-tight">{job.customerName || '—'}</p>

            {/* Bottom: dates */}
            {job.bookingDeliveryDate && (
                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                    <FiClock className="w-3 h-3 flex-shrink-0" />
                    <span>{formatDate(job.bookingDeliveryDate)}</span>
                </div>
            )}

            {isMobile && onMobileAdd && <MobileJobActions job={job}><button type="button" className="mt-2 min-h-[44px] rounded-lg border px-3 text-sm font-semibold text-blue-700" onClick={() => onMobileAdd(job)}>Add to production</button></MobileJobActions>}
            {/* Drag handle hint */}
            <div className={`absolute bottom-1 right-1.5 opacity-20 text-gray-400 ${isMobile ? 'hidden' : ''}`}>
                <svg className="w-3 h-3" viewBox="0 0 16 16" fill="currentColor">
                    <circle cx="4" cy="4" r="1.5"/>
                    <circle cx="12" cy="4" r="1.5"/>
                    <circle cx="4" cy="12" r="1.5"/>
                    <circle cx="12" cy="12" r="1.5"/>
                </svg>
            </div>
        </div>
    );
};

// ─── Filter / sort bar for the ready panel ───────────────────────────────────
const ReadyFilterBar = ({
    searchQuery, setSearchQuery,
    quoteTypeFilter, setQuoteTypeFilter,
    sortBy, setSortBy,
    urgentFilter, setUrgentFilter,
    total,
    onRefresh,
    isRefreshing,
}) => {
    const [showSort, setShowSort] = useState(false);
    const dropRef = useRef(null);

    useEffect(() => {
        const h = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setShowSort(false); };
        document.addEventListener('mousedown', h);
        return () => document.removeEventListener('mousedown', h);
    }, []);

    const SORT_OPTIONS = [
        { value: 'newest',              label: 'Newest First',  icon: <ArrowDownNarrowWide className="w-3 h-3" /> },
        { value: 'oldest',              label: 'Oldest First',  icon: <ArrowUpNarrowWide className="w-3 h-3" /> },
        { value: 'priority',            label: 'Priority',      icon: <Flame className="w-3 h-3 text-orange-500" /> },
        { value: 'alphabetical',        label: 'A → Z',         icon: <ArrowDownAZ className="w-3 h-3" /> },
        { value: 'reverse_alphabetical',label: 'Z → A',         icon: <ArrowUpAZ className="w-3 h-3" /> },
    ];

    const QUOTE_BTNS = [
        { key: 'roof',   label: 'Roof',   icon: <FiHome className="w-3 h-3" /> },
        { key: 'wall',   label: 'Wall',   icon: <BrickWall className="w-3 h-3" /> },
        { key: 'floor',  label: 'Floor',  icon: <Grid2X2 className="w-3 h-3" /> },
        { key: 'timber', label: 'Timber', icon: <GiWoodBeam className="w-3 h-3" /> },
    ];

    const currentSort = SORT_OPTIONS.find(o => o.value === sortBy) || SORT_OPTIONS[0];

    return (
        <div className="production-toolbar flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-gray-100">
            {/* Search */}
            <div className="relative min-w-[180px] max-w-xs flex-1">
                <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400" />
                <input
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search…"
                    className="w-full h-7 pl-7 pr-7 text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 bg-gray-50"
                />
                {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                        <FiX className="w-3 h-3" />
                    </button>
                )}
            </div>

            {/* Quote chips */}
            <div className="flex items-center gap-1">
                {QUOTE_BTNS.map(q => {
                    const active = quoteTypeFilter === q.key;
                    return (
                        <button
                            key={q.key}
                            onClick={() => setQuoteTypeFilter(active ? null : q.key)}
                            className={`flex items-center gap-1 h-7 px-2 rounded text-[11px] font-medium border transition-all ${
                                active
                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-gray-500 border-gray-200 hover:border-indigo-300'
                            }`}
                        >
                            {q.icon}
                            <span className="hidden md:inline">{q.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* Urgent toggle */}
            <button
                onClick={() => setUrgentFilter(v => v === 'all' ? 'urgent_quote' : 'all')}
                className={`flex items-center gap-1 h-7 px-2 rounded text-[11px] font-medium border transition-all ${
                    urgentFilter !== 'all'
                        ? 'bg-red-500 text-white border-red-500'
                        : 'bg-white text-gray-500 border-gray-200 hover:border-red-300'
                }`}
                title="Urgent only"
            >
                <Flame className="w-3 h-3" />
                <span className="hidden md:inline">Urgent</span>
            </button>

            {/* Sort dropdown */}
            <div className="relative" ref={dropRef}>
                <button
                    onClick={() => setShowSort(v => !v)}
                    className="flex items-center gap-1 h-7 px-2.5 text-[11px] font-medium border border-gray-200 rounded text-gray-600 bg-white hover:border-indigo-300 transition-all"
                >
                    {currentSort.icon}
                    <span className="hidden md:inline">{currentSort.label}</span>
                    <FiChevronDown className={`w-3 h-3 transition-transform ${showSort ? 'rotate-180' : ''}`} />
                </button>
                {showSort && (
                    <div className="absolute top-full right-0 mt-1 w-44 bg-white border border-gray-200 rounded-xl shadow-xl z-50 p-1.5 space-y-0.5">
                        {SORT_OPTIONS.map(opt => (
                            <button
                                key={opt.value}
                                onClick={() => { setSortBy(opt.value); setShowSort(false); }}
                                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-xs transition-all ${
                                    sortBy === opt.value
                                        ? 'bg-indigo-50 text-indigo-700 font-semibold'
                                        : 'text-gray-600 hover:bg-gray-50'
                                }`}
                            >
                                {opt.icon} {opt.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Refresh */}
            <button
                onClick={onRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-1 h-7 px-2 text-[11px] border border-gray-200 rounded text-gray-500 bg-white hover:border-indigo-300 transition-all disabled:opacity-50"
            >
                <FiRefreshCw className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} />
                <span className="hidden md:inline">Refresh</span>
            </button>

            <span className="ml-auto text-[11px] text-gray-400 font-medium">{total} ready</span>
        </div>
    );
};

const PAGE_SIZE = 50;

// ─── Main ProductionReadyJobs component ──────────────────────────────────────
const ProductionReadyJobs = ({
    jobs,
    isLoading,
    isRefreshing,
    onRefresh,
    onDragStart,
    onMobileAdd,
    // filter props
    searchQuery, setSearchQuery,
    quoteTypeFilter, setQuoteTypeFilter,
    sortBy, setSortBy,
    urgentFilter, setUrgentFilter,
}) => {
    const isMobile = useMobileSchedule();
    const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE);

    // Reset display limit whenever filters/sort change so the user sees fresh results from the top
    useEffect(() => {
        setDisplayLimit(PAGE_SIZE);
    }, [searchQuery, quoteTypeFilter, urgentFilter, sortBy, jobs]);

    // client-side filtering + sorting on top of server-fetched jobs
    const filteredJobs = useMemo(() => {
        let result = jobs ?? [];

        // Quote type filter
        if (quoteTypeFilter) {
            result = result.filter(j => j.quoteType === quoteTypeFilter);
        }

        // Urgent filter
        if (urgentFilter && urgentFilter !== 'all') {
            if (urgentFilter === 'urgent_quote') {
                result = result.filter(j => j.urgentQuote || j.urgentDesign);
            }
        }

        // Search
        if (searchQuery?.trim()) {
            const q = searchQuery.trim().toLowerCase();
            result = result.filter(j =>
                j.uniqueId?.toLowerCase().includes(q) ||
                j.customerName?.toLowerCase().includes(q)
            );
        }

        // Sort
        result = [...result].sort((a, b) => {
            switch (sortBy) {
                case 'oldest':
                    return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                case 'priority': {
                    const aU = a.urgentQuote || a.urgentDesign ? 1 : 0;
                    const bU = b.urgentQuote || b.urgentDesign ? 1 : 0;
                    if (bU !== aU) return bU - aU;
                    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                }
                case 'alphabetical':
                    return (a.customerName || '').localeCompare(b.customerName || '');
                case 'reverse_alphabetical':
                    return (b.customerName || '').localeCompare(a.customerName || '');
                case 'newest':
                default:
                    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
            }
        });

        return result;
    }, [jobs, searchQuery, quoteTypeFilter, urgentFilter, sortBy]);

    const visibleJobs = filteredJobs.slice(0, displayLimit);
    const hasMore = filteredJobs.length > displayLimit;

    return (
        <div className="production-ready rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            {/* Header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white">
                <div className="p-1.5 bg-indigo-100 rounded-lg">
                    <FiPackage className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                    <h3 className="text-sm font-semibold text-gray-900">Production Ready Jobs</h3>
                    <p className="text-[11px] text-gray-500">
                        {isMobile ? 'Tap Add to production on a job and choose its date' : 'Drag a card onto the production schedule table above to add it'}
                    </p>
                </div>
            </div>

            {/* Filter bar */}
            <ReadyFilterBar
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                quoteTypeFilter={quoteTypeFilter}
                setQuoteTypeFilter={setQuoteTypeFilter}
                sortBy={sortBy}
                setSortBy={setSortBy}
                urgentFilter={urgentFilter}
                setUrgentFilter={setUrgentFilter}
                total={filteredJobs.length}
                onRefresh={onRefresh}
                isRefreshing={isRefreshing}
            />

            {/* Cards grid */}
            <div className="p-4">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-7 h-7 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                        <span className="ml-3 text-sm text-gray-400">Loading ready jobs…</span>
                    </div>
                ) : visibleJobs.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                            <FiPackage className="w-5 h-5 text-gray-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-500">No production-ready jobs</p>
                        <p className="text-xs text-gray-400 mt-1">
                            Jobs appear here when their design is marked complete
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-2.5">
                        {visibleJobs.map(job => (
                            <JobCard
                                key={`${job.jobId}-${job.quoteType}-${job.quoteId}`}
                                job={job}
                                onDragStart={onDragStart}
                                onMobileAdd={onMobileAdd}
                            />
                        ))}
                    </div>
                )}

                {/* Load more */}
                {hasMore && !isLoading && (
                    <div className="flex justify-center mt-4">
                        <button
                            onClick={() => setDisplayLimit(l => l + PAGE_SIZE)}
                            className="px-4 py-2 text-xs font-medium text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors"
                        >
                            Load more ({filteredJobs.length - displayLimit} remaining)
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProductionReadyJobs;
